import { enemyById, enemiesByRegion } from "../content/enemies";
import { heroById } from "../content/heroes";
import { spellById } from "../content/meta";
import { regions } from "../content/regions";
import { towers, towerOrder } from "../content/towers";
import { addStatus, clamp, difficultyRules, mitigateDamage, scoreRun, splashMultiplier, starRating, updateStatuses, type TimedStatus } from "../systems/formulas";
import type { AudioManager } from "../systems/audio";
import type {
  ChallengeMode,
  Difficulty,
  EnemyDefinition,
  GameSettings,
  GameSnapshot,
  HeroDefinition,
  LevelDefinition,
  RunResult,
  TowerFamily,
  TowerStats,
} from "../types/game";

const WIDTH = 1280;
const HEIGHT = 720;
const FIXED_STEP = 1 / 60;

interface EnemyInstance {
  id: number;
  def: EnemyDefinition;
  hp: number;
  maxHp: number;
  shield: number;
  x: number;
  y: number;
  route: number;
  segment: number;
  progress: number;
  distance: number;
  statuses: TimedStatus[];
  attackCooldown: number;
  abilityCooldown: number;
  blockedUntil: number;
  phased: boolean;
  phaseTriggered: Set<number>;
  dead: boolean;
  leaked: boolean;
  alpha: number;
}

interface TowerInstance {
  spotIndex: number;
  family: TowerFamily;
  tier: number;
  branch?: string;
  cooldown: number;
  disabledUntil: number;
  builtAt: number;
  shots: number;
  targeting: "first" | "last" | "strongest";
}

interface Projectile {
  alive: boolean;
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  targetId: number;
  damage: number;
  damageType: "physical" | "magic" | "true";
  speed: number;
  splash: number;
  family: TowerFamily | "hero";
  branch?: string;
  color: string;
  pierce: number;
}

interface Particle {
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  kind: "spark" | "smoke" | "ring" | "leaf" | "snow" | "damage";
  text?: string;
}

interface TimedZone {
  kind: "wardlings" | "timebloom" | "hazard" | "telegraph";
  x: number;
  y: number;
  radius: number;
  createdAt: number;
  activatesAt: number;
  expiresAt: number;
  tick: number;
  color: string;
}

interface HeroState {
  def: HeroDefinition;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  hp: number;
  maxHp: number;
  level: number;
  xp: number;
  cooldown: number;
  respawnAt: number;
  attackCount: number;
  abilityCooldowns: Record<string, number>;
}

interface SpawnItem {
  time: number;
  enemyId: string;
  route: number;
}

interface Decoration {
  x: number;
  y: number;
  size: number;
  kind: number;
  phase: number;
}

export interface GameOptions {
  level: LevelDefinition;
  difficulty: Difficulty;
  mode: ChallengeMode;
  heroId: string;
  unlockedTowers: TowerFamily[];
  unlockedSpells: string[];
  upgradeRanks: Record<string, number>;
  settings: GameSettings;
  audio: AudioManager;
  onSnapshot: (snapshot: GameSnapshot) => void;
  onFinish: (result: RunResult) => void;
  onDiscover: (enemyId: string) => void;
}

export class CanvasGame {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private options: GameOptions;
  private level: LevelDefinition;
  private difficulty: Difficulty;
  private mode: ChallengeMode;
  private settings: GameSettings;
  private frame = 0;
  private accumulator = 0;
  private previousFrame = 0;
  private time = 0;
  private elapsed = 0;
  private speed = 1;
  private phase: GameSnapshot["phase"] = "briefing";
  private phaseBeforePause: GameSnapshot["phase"] = "preparing";
  private gold: number;
  private lives: number;
  private startingLives: number;
  private waveIndex = 0;
  private nextWaveIn = 12;
  private waveElapsed = 0;
  private spawnQueue: SpawnItem[] = [];
  private spawnCursor = 0;
  private enemyCounter = 0;
  private enemies: EnemyInstance[] = [];
  private towers: TowerInstance[] = [];
  private projectiles: Projectile[] = [];
  private particles: Particle[] = [];
  private zones: TimedZone[] = [];
  private selectedSpot: number | null = null;
  private targetMode: { kind: "spell"; id: string } | { kind: "hero" } | null = null;
  private spellCooldowns: Record<string, number> = { starfall: 0, wardlings: 0, timebloom: 0 };
  private hero: HeroState;
  private bossPhase = "";
  private bossActionQueue: Array<{ at: number; enemyId: number; action: NonNullable<EnemyDefinition["phases"]>[number]["action"] }> = [];
  private resultSent = false;
  private kills = 0;
  private towersBuilt = 0;
  private towersUpgraded = 0;
  private spellsCast = 0;
  private bossDefeated = false;
  private earlyWaves = 0;
  private discovered = new Set<string>();
  private message = "";
  private messageUntil = 0;
  private lastSnapshotAt = -1;
  private decorations: Decoration[] = [];
  private weather: Decoration[] = [];
  private shake = 0;
  private disposed = false;

  constructor(canvas: HTMLCanvasElement, options: GameOptions) {
    this.canvas = canvas;
    const context = canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!context) throw new Error("Canvas 2D is unavailable");
    this.context = context;
    this.options = options;
    this.level = options.level;
    this.difficulty = options.difficulty;
    this.mode = options.mode;
    this.settings = options.settings;
    const rules = difficultyRules[this.difficulty];
    const economyRanks = this.upgradeRank("economy");
    this.gold = Math.round(this.level.startingGold * rules.startingGold) + economyRanks * 25;
    this.startingLives = this.mode === "campaign" ? Math.max(1, this.level.lives + (this.difficulty === "casual" ? 3 : this.difficulty === "veteran" ? -2 : 0)) : 1;
    this.lives = this.startingLives;
    const heroDef = heroById[options.heroId] ?? heroById.brann;
    this.hero = {
      def: heroDef, x: 480, y: 350, targetX: 480, targetY: 350, hp: heroDef.health, maxHp: heroDef.health,
      level: 1, xp: 0, cooldown: 0, respawnAt: 0, attackCount: 0,
      abilityCooldowns: Object.fromEntries(heroDef.abilities.map((ability) => [ability.id, 0])),
    };
    this.configureCanvas();
    this.makeDecorations();
    this.canvas.addEventListener("pointerdown", this.onPointerDown, { passive: false });
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.options.audio.setRegion(this.level.region);
    this.publishSnapshot(true);
    this.frame = requestAnimationFrame(this.loop);
  }

  private configureCanvas(): void {
    const density = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(WIDTH * density);
    this.canvas.height = Math.round(HEIGHT * density);
    this.canvas.style.aspectRatio = `${WIDTH} / ${HEIGHT}`;
    this.context.setTransform(density, 0, 0, density, 0, 0);
    this.context.imageSmoothingEnabled = true;
  }

  private seeded(index: number): number {
    const value = Math.sin(index * 9421.421 + this.level.number * 117.17) * 43758.5453;
    return value - Math.floor(value);
  }

  private makeDecorations(): void {
    const amount = this.settings.reducedEffects ? 28 : 52;
    for (let index = 0; index < amount; index += 1) {
      this.decorations.push({ x: this.seeded(index) * WIDTH, y: 88 + this.seeded(index + 300) * 610, size: 5 + this.seeded(index + 600) * 16, kind: index % 4, phase: this.seeded(index + 900) * Math.PI * 2 });
    }
    const weatherAmount = this.settings.reducedEffects ? 18 : 42;
    for (let index = 0; index < weatherAmount; index += 1) {
      this.weather.push({ x: this.seeded(index + 1200) * WIDTH, y: this.seeded(index + 1500) * HEIGHT, size: 1 + this.seeded(index + 1800) * 3, kind: index % 3, phase: this.seeded(index + 2100) * Math.PI * 2 });
    }
  }

  private upgradeRank(branch: TowerFamily | "spells" | "economy"): number {
    return Object.keys(this.options.upgradeRanks).filter((key) => key.startsWith(`${branch}-`)).length;
  }

  private allowedTowers(): TowerFamily[] {
    if (this.mode !== "iron") return this.options.unlockedTowers;
    const selected = towerOrder[(this.level.number - 1) % towerOrder.length];
    return this.options.unlockedTowers.includes(selected) ? [selected] : [this.options.unlockedTowers[0]];
  }

  begin(): void {
    if (this.phase !== "briefing") return;
    this.phase = "preparing";
    this.nextWaveIn = 12;
    this.message = "Build your first line";
    this.messageUntil = this.time + 2.5;
    this.publishSnapshot(true);
  }

  private loop = (timestamp: number): void => {
    if (this.disposed) return;
    if (!this.previousFrame) this.previousFrame = timestamp;
    const delta = Math.min(0.1, (timestamp - this.previousFrame) / 1000);
    this.previousFrame = timestamp;
    if (!this.isPaused()) {
      this.accumulator += delta * this.speed;
      let steps = 0;
      while (this.accumulator >= FIXED_STEP && steps < 8) {
        this.update(FIXED_STEP);
        this.accumulator -= FIXED_STEP;
        steps += 1;
      }
    }
    this.render();
    this.frame = requestAnimationFrame(this.loop);
  };

  private isPaused(): boolean {
    return this.phase === "paused" || this.phase === "briefing" || this.phase === "victory" || this.phase === "defeat";
  }

  private update(delta: number): void {
    this.time += delta;
    this.elapsed += delta;
    for (const spell of Object.keys(this.spellCooldowns)) this.spellCooldowns[spell] = Math.max(0, this.spellCooldowns[spell] - delta);
    for (const ability of Object.keys(this.hero.abilityCooldowns)) this.hero.abilityCooldowns[ability] = Math.max(0, this.hero.abilityCooldowns[ability] - delta);
    if (this.phase === "preparing") {
      this.nextWaveIn = Math.max(0, this.nextWaveIn - delta);
      if (this.nextWaveIn <= 0) this.startWave(false);
    }
    if (this.phase === "wave") this.updateWave(delta);
    this.updateEnemies(delta);
    this.updateTowers(delta);
    this.updateProjectiles(delta);
    this.updateHero(delta);
    this.updateZones(delta);
    this.updateParticles(delta);
    this.updateBossActions();
    this.options.audio.setIntensity(this.enemies.some((enemy) => enemy.def.boss && !enemy.dead) ? 2 : this.phase === "wave" ? 1 : 0);
    this.publishSnapshot();
  }

  private updateWave(delta: number): void {
    this.waveElapsed += delta;
    while (this.spawnCursor < this.spawnQueue.length && this.spawnQueue[this.spawnCursor].time <= this.waveElapsed) {
      const item = this.spawnQueue[this.spawnCursor++];
      this.spawnEnemy(item.enemyId, item.route);
    }
    if (this.spawnCursor >= this.spawnQueue.length && !this.enemies.some((enemy) => !enemy.dead && !enemy.leaked)) {
      if (this.waveIndex >= this.level.waves.length) this.finish(true);
      else {
        this.phase = "preparing";
        this.nextWaveIn = Math.max(5, 11 - this.level.number * 0.12);
        this.message = `Wave ${this.waveIndex} cleared`;
        this.messageUntil = this.time + 2;
      }
    }
  }

  private startWave(early: boolean): void {
    if (this.phase !== "preparing" || this.waveIndex >= this.level.waves.length) return;
    if (early && this.nextWaveIn > 0) {
      const bonusScale = 1 + (this.upgradeRank("economy") >= 2 ? 0.2 : 0);
      const bonus = Math.ceil(this.nextWaveIn * 3 * bonusScale);
      this.gold += bonus;
      this.earlyWaves += 1;
      this.floatText(1100, 110, `+${bonus}`, "#ffd873");
    }
    const wave = this.level.waves[this.waveIndex];
    const rules = difficultyRules[this.difficulty];
    this.spawnQueue = [];
    for (const group of wave.groups) {
      const count = Math.max(1, Math.round(group.count * rules.waveDensity * (this.mode === "heroic" ? 1.12 : 1)));
      for (let index = 0; index < count; index += 1) {
        this.spawnQueue.push({ time: (group.delay ?? 0) + index * group.interval, enemyId: group.enemyId, route: (group.route ?? 0) % this.level.paths.length });
      }
    }
    this.spawnQueue.sort((a, b) => a.time - b.time);
    this.spawnCursor = 0;
    this.waveElapsed = 0;
    this.waveIndex += 1;
    this.phase = "wave";
    this.options.audio.play(wave.warning.some((id) => enemyById[id]?.boss) ? "boss" : "wave");
    if (wave.event) this.triggerEnvironmentEvent(wave.event);
    this.publishSnapshot(true);
  }

  callWaveEarly(): void { this.startWave(true); }

  private spawnEnemy(enemyId: string, route: number, segment = 0): EnemyInstance | undefined {
    const def = enemyById[enemyId];
    if (!def) return undefined;
    const rules = difficultyRules[this.difficulty];
    const healthScale = rules.health * (1 + Math.max(0, this.level.number - 1) * 0.018);
    const maxHp = Math.round(def.health * healthScale);
    const point = this.level.paths[route % this.level.paths.length][segment] ?? this.level.paths[0][0];
    const instance: EnemyInstance = {
      id: ++this.enemyCounter, def, hp: maxHp, maxHp, shield: def.traits.includes("shield") ? Math.round(maxHp * 0.28) : 0,
      x: point.x, y: point.y, route: route % this.level.paths.length, segment, progress: 0, distance: 0,
      statuses: [], attackCooldown: 0.5, abilityCooldown: 4 + this.seeded(this.enemyCounter) * 4, blockedUntil: 0,
      phased: false, phaseTriggered: new Set<number>(), dead: false, leaked: false, alpha: 0,
    };
    this.enemies.push(instance);
    if (!this.discovered.has(def.id)) {
      this.discovered.add(def.id);
      this.options.onDiscover(def.id);
      this.message = `New: ${def.name}`;
      this.messageUntil = this.time + 2.4;
    }
    for (let index = 0; index < 7; index += 1) this.emitParticle(point.x, point.y, def.accent, "smoke", 0.5);
    return instance;
  }

  private updateEnemies(delta: number): void {
    for (const enemy of this.enemies) {
      if (enemy.dead || enemy.leaked) continue;
      enemy.alpha = Math.min(1, enemy.alpha + delta * 4);
      enemy.statuses = updateStatuses(enemy.statuses, delta);
      const poison = enemy.statuses.find((status) => status.kind === "poison");
      const burn = enemy.statuses.find((status) => status.kind === "burn");
      if (poison) this.damageEnemy(enemy, poison.power * poison.stacks * delta, "true", false);
      if (burn) this.damageEnemy(enemy, burn.power * burn.stacks * delta, "magic", false);
      if (enemy.dead) continue;
      if (enemy.def.traits.includes("regenerate") && !burn) enemy.hp = Math.min(enemy.maxHp, enemy.hp + enemy.maxHp * 0.012 * delta);
      enemy.abilityCooldown -= delta;
      if (enemy.abilityCooldown <= 0) this.enemyAbility(enemy);
      this.checkBossPhases(enemy);

      const stunned = enemy.statuses.some((status) => status.kind === "stun" || status.kind === "root");
      let blocked = enemy.blockedUntil > this.time;
      const heroDistance = Math.hypot(enemy.x - this.hero.x, enemy.y - this.hero.y);
      if (!enemy.def.traits.includes("flying") && this.hero.hp > 0 && heroDistance < 42) {
        blocked = true;
        enemy.blockedUntil = this.time + 0.12;
        enemy.attackCooldown -= delta;
        if (enemy.attackCooldown <= 0) {
          const dealt = mitigateDamage(enemy.def.damage, "physical", this.hero.def.armor, this.hero.def.magicResist);
          this.hero.hp = Math.max(0, this.hero.hp - dealt);
          enemy.attackCooldown = 1.1;
          this.floatText(this.hero.x, this.hero.y - 25, `-${dealt}`, "#ff8a70");
          this.options.audio.play("hit", (this.hero.x / WIDTH) * 2 - 1);
          if (this.hero.hp <= 0) this.hero.respawnAt = this.time + this.hero.def.respawn;
        }
      }
      if (stunned || blocked) continue;
      const slow = enemy.statuses.filter((status) => status.kind === "slow").reduce((strongest, status) => Math.max(strongest, status.power), 0);
      const buffer = this.enemies.some((other) => other !== enemy && !other.dead && other.def.traits.includes("buffer") && Math.hypot(other.x - enemy.x, other.y - enemy.y) < 110) ? 1.12 : 1;
      const phasedBoost = enemy.phased ? 1.22 : 1;
      const speed = enemy.def.speed * difficultyRules[this.difficulty].speed * (1 - clamp(slow, 0, 0.75)) * buffer * phasedBoost;
      this.moveAlongPath(enemy, speed * delta);
    }
    if (this.enemies.length > 280) this.enemies = this.enemies.filter((enemy) => !enemy.dead && !enemy.leaked);
  }

  private moveAlongPath(enemy: EnemyInstance, amount: number): void {
    const path = this.level.paths[enemy.route];
    let remaining = amount;
    while (remaining > 0 && enemy.segment < path.length - 1) {
      const from = path[enemy.segment];
      const to = path[enemy.segment + 1];
      const length = Math.hypot(to.x - from.x, to.y - from.y);
      const segmentRemaining = length * (1 - enemy.progress);
      if (remaining >= segmentRemaining) {
        remaining -= segmentRemaining;
        enemy.segment += 1;
        enemy.progress = 0;
        enemy.x = to.x;
        enemy.y = to.y;
      } else {
        enemy.progress += remaining / length;
        enemy.x = from.x + (to.x - from.x) * enemy.progress;
        enemy.y = from.y + (to.y - from.y) * enemy.progress;
        remaining = 0;
      }
      enemy.distance += amount;
    }
    if (enemy.segment >= path.length - 1) this.leakEnemy(enemy);
  }

  private enemyAbility(enemy: EnemyInstance): void {
    enemy.abilityCooldown = enemy.def.boss ? 7 : 9 + this.seeded(enemy.id + Math.floor(this.time)) * 5;
    if (enemy.def.traits.includes("healer")) {
      for (const ally of this.enemies) if (!ally.dead && Math.hypot(ally.x - enemy.x, ally.y - enemy.y) < 125) ally.hp = Math.min(ally.maxHp, ally.hp + ally.maxHp * 0.1);
      this.ring(enemy.x, enemy.y, "#80edaa", 1);
    }
    if (enemy.def.traits.includes("summoner") && !enemy.def.boss) {
      const minion = enemiesByRegion(enemy.def.region)[0];
      if (minion) {
        this.spawnEnemy(minion.id, enemy.route, Math.max(0, enemy.segment - 1));
        this.spawnEnemy(minion.id, enemy.route, Math.max(0, enemy.segment - 1));
      }
      this.ring(enemy.x, enemy.y, enemy.def.accent, 1.2);
    }
    if (enemy.def.traits.includes("disruptor")) {
      const closest = this.nearestTower(enemy.x, enemy.y, 155);
      if (closest) {
        closest.disabledUntil = this.time + 2.5;
        this.ring(this.level.buildSpots[closest.spotIndex].x, this.level.buildSpots[closest.spotIndex].y, "#c377ff", 1.2);
      }
    }
    if (enemy.def.traits.includes("teleport") && !enemy.def.boss && enemy.segment < this.level.paths[enemy.route].length - 2) {
      enemy.segment += 1;
      enemy.progress = 0;
      const point = this.level.paths[enemy.route][enemy.segment];
      enemy.x = point.x; enemy.y = point.y;
      this.ring(point.x, point.y, "#c477ff", 0.8);
    }
  }

  private checkBossPhases(enemy: EnemyInstance): void {
    if (!enemy.def.boss || !enemy.def.phases) return;
    enemy.def.phases.forEach((phase, index) => {
      if (enemy.hp / enemy.maxHp <= phase.threshold && !enemy.phaseTriggered.has(index)) {
        enemy.phaseTriggered.add(index);
        this.bossPhase = phase.title[this.options.settings.language] || phase.title.en;
        this.bossActionQueue.push({ at: this.time + phase.telegraph, enemyId: enemy.id, action: phase.action });
        this.zones.push({ kind: "telegraph", x: enemy.x, y: enemy.y, radius: 115 + index * 18, createdAt: this.time, activatesAt: this.time + phase.telegraph, expiresAt: this.time + phase.telegraph + 0.3, tick: 0, color: enemy.def.accent });
        this.options.audio.play("boss", (enemy.x / WIDTH) * 2 - 1);
      }
    });
    if (!enemy.phased && enemy.hp < enemy.maxHp * 0.5) enemy.phased = true;
  }

  private updateBossActions(): void {
    for (const queued of this.bossActionQueue.filter((entry) => entry.at <= this.time)) {
      const boss = this.enemies.find((enemy) => enemy.id === queued.enemyId && !enemy.dead);
      if (!boss) continue;
      if (queued.action === "summon") {
        const roster = enemiesByRegion(boss.def.region);
        for (let index = 0; index < 6; index += 1) this.spawnEnemy(roster[index % Math.min(3, roster.length)].id, index % this.level.paths.length, Math.max(0, boss.segment - 1));
      } else if (queued.action === "shield") {
        boss.shield += boss.maxHp * 0.22;
      } else if (queued.action === "freezeTowers") {
        for (const tower of this.towers) tower.disabledUntil = Math.max(tower.disabledUntil, this.time + 4);
      } else if (queued.action === "quake" || queued.action === "hazard") {
        for (let index = 0; index < 3; index += 1) {
          const spot = this.level.buildSpots[(boss.id + index * 3) % this.level.buildSpots.length];
          this.zones.push({ kind: "hazard", x: spot.x, y: spot.y, radius: 82, createdAt: this.time, activatesAt: this.time + 1.6, expiresAt: this.time + 5.5, tick: 0, color: queued.action === "quake" ? "#ff7a43" : "#b56cff" });
        }
        this.shake = 10;
      } else if (queued.action === "portal") {
        boss.segment = Math.min(this.level.paths[boss.route].length - 2, boss.segment + 1);
        boss.progress = 0;
      } else if (queued.action === "flight") {
        boss.blockedUntil = 0;
        boss.abilityCooldown = 3;
      }
      this.ring(boss.x, boss.y, boss.def.accent, 1.4);
    }
    this.bossActionQueue = this.bossActionQueue.filter((entry) => entry.at > this.time);
  }

  private leakEnemy(enemy: EnemyInstance): void {
    if (enemy.dead || enemy.leaked) return;
    enemy.leaked = true;
    this.lives = Math.max(0, this.lives - enemy.def.lives);
    this.floatText(1180, 350, `-${enemy.def.lives} ♥`, "#ff756f");
    this.options.audio.play("error", 0.8);
    if (this.lives <= 0) this.finish(false);
  }

  private towerStats(tower: TowerInstance): TowerStats {
    const definition = towers[tower.family];
    const base = tower.branch ? definition.branches.find((branch) => branch.id === tower.branch)?.stats ?? definition.tiers[2].stats : definition.tiers[Math.min(2, tower.tier - 1)].stats;
    const damageRank = this.upgradeRank(tower.family);
    const rangeRank = damageRank >= 2 ? 0.06 : 0;
    return { ...base, damage: Math.round(base.damage * (1 + damageRank * 0.05)), range: base.range * (1 + rangeRank) };
  }

  private updateTowers(delta: number): void {
    for (const tower of this.towers) {
      tower.cooldown -= delta;
      if (tower.disabledUntil > this.time || tower.cooldown > 0) continue;
      const stats = this.towerStats(tower);
      const point = this.level.buildSpots[tower.spotIndex];
      const aura = this.towers.some((other) => other.branch === "dawnkeep" && Math.hypot(this.level.buildSpots[other.spotIndex].x - point.x, this.level.buildSpots[other.spotIndex].y - point.y) < 170) || (this.hero.def.id === "puck" && this.hero.hp > 0 && Math.hypot(this.hero.x - point.x, this.hero.y - point.y) < 175);
      const rate = stats.rate * (aura ? 0.86 : 1);
      if (tower.family === "guardians") {
        const candidates = this.targetCandidates(point.x, point.y, stats.range, false).slice(0, stats.block ?? 2);
        if (candidates.length) {
          for (const enemy of candidates) {
            enemy.blockedUntil = this.time + rate + 0.12;
            this.damageEnemy(enemy, stats.damage * (tower.branch === "wildclaw" ? 1 + candidates.length * 0.12 : 1), "physical");
            if (tower.branch === "wildclaw" && tower.shots % 4 === 0) this.rewindEnemy(enemy, 32);
          }
          tower.shots += 1;
          tower.cooldown = rate;
          this.options.audio.play("sword", (point.x / WIDTH) * 2 - 1);
        }
        continue;
      }
      const candidates = this.targetCandidates(point.x, point.y, stats.range, tower.family !== "artillery");
      const target = this.chooseTarget(candidates, tower.targeting);
      if (!target) continue;
      tower.shots += 1;
      const damageType = towers[tower.family].damageType;
      this.obtainProjectile({
        x: point.x, y: point.y - 26, targetId: target.id, damage: stats.damage, damageType, speed: stats.projectileSpeed,
        splash: stats.splash ?? 0, family: tower.family, branch: tower.branch,
        color: tower.branch ? towers[tower.family].branches.find((branch) => branch.id === tower.branch)?.color ?? towers[tower.family].color : towers[tower.family].color,
        pierce: tower.branch === "skyhunter" ? 3 : 1,
      });
      tower.cooldown = rate;
      this.options.audio.play(tower.family === "rangers" ? "arrow" : tower.family === "arcanists" ? "magic" : "blast", (point.x / WIDTH) * 2 - 1);
    }
  }

  private targetCandidates(x: number, y: number, range: number, allowFlying: boolean): EnemyInstance[] {
    return this.enemies.filter((enemy) => {
      if (enemy.dead || enemy.leaked || (!allowFlying && enemy.def.traits.includes("flying"))) return false;
      const distance = Math.hypot(enemy.x - x, enemy.y - y);
      if (distance > range) return false;
      if (enemy.def.traits.includes("stealth") && distance > (this.upgradeRank("rangers") >= 4 ? 130 : 78)) return false;
      return true;
    });
  }

  private chooseTarget(candidates: EnemyInstance[], targeting: TowerInstance["targeting"]): EnemyInstance | undefined {
    if (targeting === "strongest") return candidates.sort((a, b) => b.hp - a.hp)[0];
    if (targeting === "last") return candidates.sort((a, b) => a.distance - b.distance)[0];
    return candidates.sort((a, b) => b.distance - a.distance)[0];
  }

  private obtainProjectile(seed: Omit<Projectile, "alive" | "previousX" | "previousY">): void {
    let projectile = this.projectiles.find((entry) => !entry.alive);
    if (!projectile) {
      projectile = { alive: false, previousX: seed.x, previousY: seed.y, ...seed };
      this.projectiles.push(projectile);
    }
    Object.assign(projectile, seed, { alive: true, previousX: seed.x, previousY: seed.y });
  }

  private updateProjectiles(delta: number): void {
    for (const projectile of this.projectiles) {
      if (!projectile.alive) continue;
      let target = this.enemies.find((enemy) => enemy.id === projectile.targetId && !enemy.dead && !enemy.leaked);
      if (!target) target = this.chooseTarget(this.targetCandidates(projectile.x, projectile.y, 180, true), "first");
      if (!target) { projectile.alive = false; continue; }
      projectile.targetId = target.id;
      projectile.previousX = projectile.x;
      projectile.previousY = projectile.y;
      const dx = target.x - projectile.x;
      const dy = target.y - projectile.y;
      const distance = Math.hypot(dx, dy);
      const move = projectile.speed * delta;
      if (distance <= move + target.def.size * 0.4) {
        this.hitProjectile(projectile, target);
      } else {
        projectile.x += (dx / distance) * move;
        projectile.y += (dy / distance) * move;
      }
    }
  }

  private hitProjectile(projectile: Projectile, target: EnemyInstance): void {
    if (projectile.splash > 0) {
      for (const enemy of this.enemies) {
        if (enemy.dead || enemy.leaked || enemy.def.traits.includes("flying")) continue;
        const distance = Math.hypot(enemy.x - target.x, enemy.y - target.y);
        const multiplier = splashMultiplier(distance, projectile.splash);
        if (multiplier > 0) this.damageEnemy(enemy, projectile.damage * multiplier, projectile.damageType);
      }
      this.ring(target.x, target.y, projectile.color, 0.45, projectile.splash);
      if (projectile.branch === "emberforge") for (const enemy of this.targetCandidates(target.x, target.y, projectile.splash, false)) enemy.statuses = addStatus(enemy.statuses, { kind: "burn", remaining: 4, power: 9 }, enemy.def.traits.includes("statusImmune"));
      if (projectile.branch === "frostmortar") for (const enemy of this.targetCandidates(target.x, target.y, projectile.splash, false)) enemy.statuses = addStatus(enemy.statuses, { kind: "slow", remaining: 3.5, power: 0.42 }, enemy.def.traits.includes("statusImmune"));
    } else {
      const critical = projectile.branch === "thornwatch" && this.towers.some((tower) => tower.branch === "thornwatch" && tower.shots % 5 === 0);
      this.damageEnemy(target, projectile.damage * (critical ? 2 : 1), projectile.damageType);
      if (critical) this.floatText(target.x, target.y - 30, "CRIT", "#f8ed8e");
    }
    if (!target.dead) this.applyBranchEffect(projectile, target);
    projectile.pierce -= 1;
    if (projectile.pierce > 0) {
      const next = this.targetCandidates(target.x, target.y, 115, true).find((enemy) => enemy.id !== target.id);
      if (next) { projectile.targetId = next.id; projectile.x = target.x; projectile.y = target.y; return; }
    }
    projectile.alive = false;
    for (let index = 0; index < (this.settings.reducedEffects ? 3 : 7); index += 1) this.emitParticle(target.x, target.y, projectile.color, "spark", 0.35);
  }

  private applyBranchEffect(projectile: Projectile, target: EnemyInstance): void {
    const immune = target.def.traits.includes("statusImmune");
    if (projectile.branch === "thornwatch") {
      target.statuses = addStatus(target.statuses, { kind: "poison", remaining: 4, power: 3.8 }, immune);
      target.statuses = addStatus(target.statuses, { kind: "mark", remaining: 5, power: 0.14 }, false);
    } else if (projectile.branch === "skyhunter") {
      if (target.def.traits.includes("flying")) this.damageEnemy(target, projectile.damage, "physical");
      if (!target.def.boss && target.hp / target.maxHp < 0.08) this.damageEnemy(target, target.hp + 1, "true");
    } else if (projectile.branch === "stormspire") {
      target.statuses = addStatus(target.statuses, { kind: "magicBreak", remaining: 5, power: 0.18 }, immune);
      const chains = this.targetCandidates(target.x, target.y, 120, true).filter((enemy) => enemy.id !== target.id).slice(0, 3);
      chains.forEach((enemy, index) => this.damageEnemy(enemy, projectile.damage * (0.55 - index * 0.1), "magic"));
      if (this.seeded(projectile.targetId + Math.floor(this.time)) > 0.78) this.rewindEnemy(target, 65);
    } else if (projectile.branch === "chronicle") {
      target.statuses = addStatus(target.statuses, { kind: "slow", remaining: 3.2, power: 0.38 }, immune);
      if (this.seeded(projectile.targetId + Math.floor(this.time * 2)) > 0.88) this.rewindEnemy(target, 85);
    } else if (projectile.branch === "frostmortar" && this.seeded(projectile.targetId + Math.floor(this.time)) > 0.84) {
      target.statuses = addStatus(target.statuses, { kind: "stun", remaining: 1.1, power: 1 }, immune);
    }
  }

  private damageEnemy(enemy: EnemyInstance, amount: number, damageType: "physical" | "magic" | "true", show = true): void {
    if (enemy.dead || enemy.leaked) return;
    const armorBreak = enemy.statuses.find((status) => status.kind === "armorBreak")?.power ?? 0;
    const magicBreak = enemy.statuses.find((status) => status.kind === "magicBreak")?.power ?? 0;
    const marked = enemy.statuses.some((status) => status.kind === "mark") ? 1.14 : 1;
    let dealt = mitigateDamage(amount * marked, damageType, enemy.def.armor + difficultyRules[this.difficulty].armor - armorBreak, enemy.def.magicResist - magicBreak);
    if (enemy.shield > 0) {
      const absorbed = Math.min(enemy.shield, dealt);
      enemy.shield -= absorbed;
      dealt -= absorbed;
    }
    enemy.hp -= dealt;
    if (show && dealt > 0 && (dealt > 25 || this.seeded(enemy.id + Math.floor(this.time * 10)) > 0.72)) this.floatText(enemy.x, enemy.y - enemy.def.size, `${Math.round(dealt)}`, damageType === "magic" ? "#8ce8ff" : damageType === "true" ? "#e9a2ff" : "#fff0c7");
    if (enemy.hp <= 0) this.killEnemy(enemy);
  }

  private killEnemy(enemy: EnemyInstance): void {
    if (enemy.dead) return;
    enemy.dead = true;
    const bounty = Math.round(enemy.def.bounty * difficultyRules[this.difficulty].bounty * (enemy.def.boss && this.upgradeRank("economy") >= 4 ? 1.15 : 1));
    this.gold += bounty;
    this.kills += 1;
    if (enemy.def.boss) this.bossDefeated = true;
    this.hero.xp += enemy.def.boss ? 200 : 8 + enemy.def.lives * 2;
    const nextLevel = Math.min(10, 1 + Math.floor(this.hero.xp / 110));
    if (nextLevel > this.hero.level) {
      this.hero.level = nextLevel;
      this.hero.maxHp = Math.round(this.hero.def.health * (1 + (nextLevel - 1) * 0.08));
      this.hero.hp = this.hero.maxHp;
      this.floatText(this.hero.x, this.hero.y - 40, `LEVEL ${nextLevel}`, "#ffe47a");
    }
    if (enemy.def.traits.includes("split") && !enemy.def.boss) {
      const small = enemiesByRegion(enemy.def.region)[0];
      for (let index = 0; index < 2; index += 1) {
        const child = this.spawnEnemy(small.id, enemy.route, Math.max(0, enemy.segment));
        if (child) { child.progress = enemy.progress; child.x = enemy.x + (index ? 12 : -12); child.y = enemy.y; child.hp *= 0.55; child.maxHp = child.hp; }
      }
    }
    this.options.audio.play(enemy.def.boss ? "victory" : "hit", (enemy.x / WIDTH) * 2 - 1);
    for (let index = 0; index < (this.settings.reducedEffects ? 6 : 14); index += 1) this.emitParticle(enemy.x, enemy.y, enemy.def.accent, index % 3 === 0 ? "smoke" : "spark", 0.7);
  }

  private rewindEnemy(enemy: EnemyInstance, pixels: number): void {
    let remaining = pixels;
    const path = this.level.paths[enemy.route];
    while (remaining > 0 && (enemy.segment > 0 || enemy.progress > 0)) {
      const from = path[enemy.segment];
      const to = path[Math.min(path.length - 1, enemy.segment + 1)];
      const length = Math.hypot(to.x - from.x, to.y - from.y);
      const traversed = length * enemy.progress;
      if (remaining >= traversed && enemy.segment > 0) {
        remaining -= traversed;
        enemy.segment -= 1;
        enemy.progress = 1;
      } else {
        enemy.progress = Math.max(0, enemy.progress - remaining / Math.max(1, length));
        remaining = 0;
      }
    }
    const from = path[enemy.segment], to = path[Math.min(path.length - 1, enemy.segment + 1)];
    enemy.x = from.x + (to.x - from.x) * enemy.progress;
    enemy.y = from.y + (to.y - from.y) * enemy.progress;
    enemy.distance = Math.max(0, enemy.distance - pixels);
    this.ring(enemy.x, enemy.y, "#b37af2", 0.5);
  }

  private updateHero(delta: number): void {
    if (this.hero.hp <= 0) {
      if (this.hero.respawnAt > 0 && this.time >= this.hero.respawnAt) {
        this.hero.hp = this.hero.maxHp;
        this.hero.x = 470; this.hero.y = 350; this.hero.targetX = 470; this.hero.targetY = 350; this.hero.respawnAt = 0;
        this.ring(this.hero.x, this.hero.y, this.hero.def.accent, 1.2);
      }
      return;
    }
    const dx = this.hero.targetX - this.hero.x, dy = this.hero.targetY - this.hero.y, distance = Math.hypot(dx, dy);
    if (distance > 4) {
      const move = Math.min(distance, this.hero.def.speed * delta);
      this.hero.x += dx / distance * move; this.hero.y += dy / distance * move;
      if (!this.settings.reducedEffects && this.seeded(Math.floor(this.time * 20)) > 0.72) this.emitParticle(this.hero.x, this.hero.y + 12, this.hero.def.color, "smoke", 0.3);
    } else if (this.hero.def.id === "brann") {
      this.hero.hp = Math.min(this.hero.maxHp, this.hero.hp + this.hero.maxHp * 0.012 * delta);
    }
    this.hero.cooldown -= delta;
    if (this.hero.cooldown > 0) return;
    const target = this.chooseTarget(this.targetCandidates(this.hero.x, this.hero.y, this.hero.def.range + (this.hero.level - 1) * 2, this.hero.def.role !== "tank"), "first");
    if (!target) return;
    this.hero.attackCount += 1;
    const damage = this.hero.def.damage * (1 + (this.hero.level - 1) * 0.1) * (this.hero.def.id === "ilyra" && this.hero.attackCount % 4 === 0 ? 2 : 1);
    if (this.hero.def.range > 90) {
      this.obtainProjectile({ x: this.hero.x, y: this.hero.y - 16, targetId: target.id, damage, damageType: this.hero.def.role === "controller" ? "magic" : "physical", speed: 560, splash: 0, family: "hero", color: this.hero.def.accent, pierce: this.hero.def.id === "nym" ? 2 : 1 });
    } else {
      target.blockedUntil = this.time + this.hero.def.attackRate + 0.1;
      this.damageEnemy(target, damage, "physical");
    }
    this.hero.cooldown = this.hero.def.attackRate;
  }

  castHeroAbility(id: string): boolean {
    const ability = this.hero.def.abilities.find((entry) => entry.id === id);
    if (!ability || ability.cooldown <= 0 || this.hero.abilityCooldowns[id] > 0 || this.hero.hp <= 0 || this.isPaused()) return false;
    const nearby = this.targetCandidates(this.hero.x, this.hero.y, 145, true);
    if (id === "shieldring") this.hero.hp = Math.min(this.hero.maxHp, this.hero.hp + this.hero.maxHp * 0.32);
    else if (id === "bellstrike") nearby.forEach((enemy) => { enemy.statuses = addStatus(enemy.statuses, { kind: "stun", remaining: 1.5, power: 1 }, enemy.def.traits.includes("statusImmune")); this.damageEnemy(enemy, 65, "physical"); });
    else if (id === "sunthread") nearby.forEach((enemy) => this.damageEnemy(enemy, 120, "physical"));
    else if (id === "falcondive") {
      const strongest = nearby.sort((a, b) => b.maxHp - a.maxHp)[0];
      if (strongest) strongest.statuses = addStatus(strongest.statuses, { kind: "mark", remaining: 8, power: 0.2 });
    } else if (id === "cloudsnare") nearby.forEach((enemy) => enemy.statuses = addStatus(enemy.statuses, { kind: "root", remaining: 2.2, power: 1 }, enemy.def.traits.includes("statusImmune")));
    else if (id === "littletempest") nearby.forEach((enemy, index) => this.damageEnemy(enemy, Math.max(35, 100 - index * 8), "magic"));
    else if (id === "seedfriends") this.zones.push({ kind: "wardlings", x: this.hero.x, y: this.hero.y, radius: 90, createdAt: this.time, activatesAt: this.time, expiresAt: this.time + 12, tick: 0, color: "#8be276" });
    else if (id === "enormousacorn") nearby.forEach((enemy) => { this.damageEnemy(enemy, 145, "physical"); enemy.statuses = addStatus(enemy.statuses, { kind: "stun", remaining: 1, power: 1 }, enemy.def.traits.includes("statusImmune")); });
    this.hero.abilityCooldowns[id] = ability.cooldown;
    this.ring(this.hero.x, this.hero.y, this.hero.def.accent, 0.9, 145);
    this.options.audio.play("spell", (this.hero.x / WIDTH) * 2 - 1);
    this.publishSnapshot(true);
    return true;
  }

  private updateZones(delta: number): void {
    for (const zone of this.zones) {
      if (this.time < zone.activatesAt || this.time > zone.expiresAt) continue;
      zone.tick -= delta;
      if (zone.kind === "wardlings" && zone.tick <= 0) {
        const targets = this.targetCandidates(zone.x, zone.y, zone.radius, false).slice(0, 3);
        targets.forEach((enemy) => { enemy.blockedUntil = this.time + 0.55; this.damageEnemy(enemy, 20, "physical"); });
        zone.tick = 0.5;
      } else if (zone.kind === "timebloom") {
        this.targetCandidates(zone.x, zone.y, zone.radius, true).forEach((enemy) => enemy.statuses = addStatus(enemy.statuses, { kind: "slow", remaining: 0.3, power: 0.64 }, enemy.def.traits.includes("statusImmune")));
      } else if (zone.kind === "hazard") {
        for (const tower of this.towers) {
          const point = this.level.buildSpots[tower.spotIndex];
          if (Math.hypot(point.x - zone.x, point.y - zone.y) < zone.radius) tower.disabledUntil = Math.max(tower.disabledUntil, this.time + 0.2);
        }
        if (this.hero.hp > 0 && Math.hypot(this.hero.x - zone.x, this.hero.y - zone.y) < zone.radius && zone.tick <= 0) {
          this.hero.hp = Math.max(0, this.hero.hp - 25);
          zone.tick = 0.8;
        }
      }
    }
    this.zones = this.zones.filter((zone) => zone.expiresAt > this.time);
  }

  private triggerEnvironmentEvent(event: NonNullable<LevelDefinition["waves"]>[number]["event"]): void {
    if (!event) return;
    this.message = event.toUpperCase();
    this.messageUntil = this.time + 3;
    if (event === "frozen") {
      const tower = this.towers[Math.floor(this.seeded(this.waveIndex) * this.towers.length)];
      if (tower) tower.disabledUntil = this.time + 4;
    } else if (event === "eruption") {
      const spot = this.level.buildSpots[Math.floor(this.seeded(this.waveIndex) * this.level.buildSpots.length)];
      this.zones.push({ kind: "hazard", x: spot.x, y: spot.y, radius: 85, createdAt: this.time, activatesAt: this.time + 2, expiresAt: this.time + 6, tick: 0, color: "#ff6b32" });
    } else if (event === "rift") {
      for (const enemy of this.enemies.filter((entry) => !entry.dead).slice(0, 4)) this.rewindEnemy(enemy, this.seeded(enemy.id) > 0.5 ? 90 : -50);
    }
  }

  activateSpell(id: string): boolean {
    if (!this.options.unlockedSpells.includes(id) || this.spellCooldowns[id] > 0 || this.isPaused()) return false;
    this.targetMode = this.targetMode?.kind === "spell" && this.targetMode.id === id ? null : { kind: "spell", id };
    this.message = this.targetMode ? "Choose a target" : "";
    this.messageUntil = this.time + 2;
    this.publishSnapshot(true);
    return true;
  }

  activateHeroMove(): void {
    if (this.hero.hp <= 0 || this.isPaused()) return;
    this.targetMode = this.targetMode?.kind === "hero" ? null : { kind: "hero" };
    this.publishSnapshot(true);
  }

  private castSpellAt(id: string, x: number, y: number): void {
    const spell = spellById[id];
    if (!spell || this.spellCooldowns[id] > 0) return;
    const radius = spell.radius * (1 + (this.upgradeRank("spells") >= 2 ? 0.08 : 0));
    if (id === "starfall") {
      const hits = this.upgradeRank("spells") >= 3 ? 9 : 7;
      for (let index = 0; index < hits; index += 1) {
        const angle = index / hits * Math.PI * 2;
        const sx = x + Math.cos(angle) * radius * 0.45, sy = y + Math.sin(angle) * radius * 0.45;
        this.targetCandidates(sx, sy, radius * 0.55, true).forEach((enemy) => this.damageEnemy(enemy, 96 + this.level.number * 3, "true"));
        this.ring(sx, sy, spell.color, 0.55, 48);
      }
      this.shake = 8;
    } else if (id === "wardlings") {
      this.zones.push({ kind: "wardlings", x, y, radius, createdAt: this.time, activatesAt: this.time, expiresAt: this.time + 14 + (this.upgradeRank("spells") >= 4 ? 5 : 0), tick: 0, color: spell.color });
    } else if (id === "timebloom") {
      this.zones.push({ kind: "timebloom", x, y, radius, createdAt: this.time, activatesAt: this.time, expiresAt: this.time + 7, tick: 0, color: spell.color });
    }
    const recovery = this.upgradeRank("spells") >= 1 ? 0.95 : 1;
    this.spellCooldowns[id] = spell.cooldown * recovery;
    this.spellsCast += 1;
    this.targetMode = null;
    this.options.audio.play("spell", (x / WIDTH) * 2 - 1);
    this.ring(x, y, spell.color, 1.1, radius);
    this.publishSnapshot(true);
  }

  buildTower(family: TowerFamily): boolean {
    if (this.selectedSpot === null || this.towers.some((tower) => tower.spotIndex === this.selectedSpot) || !this.allowedTowers().includes(family)) return false;
    const definition = towers[family];
    if (this.gold < definition.baseCost) { this.errorMessage("Not enough gold"); return false; }
    this.gold -= definition.baseCost;
    this.towers.push({ spotIndex: this.selectedSpot, family, tier: 1, cooldown: 0.2, disabledUntil: 0, builtAt: this.time, shots: 0, targeting: "first" });
    this.towersBuilt += 1;
    this.options.audio.play("build", (this.level.buildSpots[this.selectedSpot].x / WIDTH) * 2 - 1);
    this.publishSnapshot(true);
    return true;
  }

  upgradeSelectedTower(): boolean {
    const tower = this.selectedTower();
    if (!tower || tower.tier >= 3 || tower.branch) return false;
    const cost = towers[tower.family].tiers[tower.tier].cost;
    if (this.gold < cost) { this.errorMessage("Not enough gold"); return false; }
    this.gold -= cost;
    tower.tier += 1;
    tower.builtAt = this.time;
    this.towersUpgraded += 1;
    this.options.audio.play("upgrade", (this.level.buildSpots[tower.spotIndex].x / WIDTH) * 2 - 1);
    this.publishSnapshot(true);
    return true;
  }

  specializeSelectedTower(branchId: string): boolean {
    const tower = this.selectedTower();
    if (!tower || tower.tier < 3 || tower.branch) return false;
    const branch = towers[tower.family].branches.find((entry) => entry.id === branchId);
    if (!branch) return false;
    if (this.gold < branch.cost) { this.errorMessage("Not enough gold"); return false; }
    this.gold -= branch.cost;
    tower.branch = branch.id;
    tower.builtAt = this.time;
    this.towersUpgraded += 1;
    this.options.audio.play("upgrade", (this.level.buildSpots[tower.spotIndex].x / WIDTH) * 2 - 1);
    this.ring(this.level.buildSpots[tower.spotIndex].x, this.level.buildSpots[tower.spotIndex].y, branch.color, 1.1, 85);
    this.publishSnapshot(true);
    return true;
  }

  sellSelectedTower(): boolean {
    const tower = this.selectedTower();
    if (!tower) return false;
    const refundRate = 0.7 + (this.upgradeRank("economy") >= 3 ? 0.1 : 0);
    const refund = Math.floor(this.towerInvestment(tower) * refundRate);
    this.gold += refund;
    this.towers = this.towers.filter((entry) => entry !== tower);
    this.options.audio.play("sell", (this.level.buildSpots[tower.spotIndex].x / WIDTH) * 2 - 1);
    this.floatText(this.level.buildSpots[tower.spotIndex].x, this.level.buildSpots[tower.spotIndex].y, `+${refund}`, "#ffe17e");
    this.publishSnapshot(true);
    return true;
  }

  cycleTargeting(): void {
    const tower = this.selectedTower();
    if (!tower) return;
    tower.targeting = tower.targeting === "first" ? "strongest" : tower.targeting === "strongest" ? "last" : "first";
    this.message = `Target: ${tower.targeting}`;
    this.messageUntil = this.time + 1.5;
    this.publishSnapshot(true);
  }

  private towerInvestment(tower: TowerInstance): number {
    const definition = towers[tower.family];
    let amount = definition.baseCost;
    for (let tier = 1; tier < tower.tier; tier += 1) amount += definition.tiers[tier].cost;
    if (tower.branch) amount += definition.branches.find((branch) => branch.id === tower.branch)?.cost ?? 0;
    return amount;
  }

  private selectedTower(): TowerInstance | undefined {
    return this.selectedSpot === null ? undefined : this.towers.find((tower) => tower.spotIndex === this.selectedSpot);
  }

  private nearestTower(x: number, y: number, range: number): TowerInstance | undefined {
    return this.towers.map((tower) => ({ tower, distance: Math.hypot(this.level.buildSpots[tower.spotIndex].x - x, this.level.buildSpots[tower.spotIndex].y - y) })).filter((entry) => entry.distance <= range).sort((a, b) => a.distance - b.distance)[0]?.tower;
  }

  private errorMessage(message: string): void {
    this.message = message;
    this.messageUntil = this.time + 1.8;
    this.options.audio.play("error");
    this.publishSnapshot(true);
  }

  togglePause(): void {
    if (["victory", "defeat", "briefing"].includes(this.phase)) return;
    if (this.phase === "paused") this.phase = this.phaseBeforePause;
    else { this.phaseBeforePause = this.phase; this.phase = "paused"; }
    this.publishSnapshot(true);
  }

  setSpeed(value: number): void {
    this.speed = [1, 2, 3].includes(value) ? value : 1;
    this.publishSnapshot(true);
  }

  updateSettings(settings: GameSettings): void { this.settings = settings; }

  private onVisibilityChange = (): void => {
    if (document.hidden && !this.isPaused()) {
      this.phaseBeforePause = this.phase;
      this.phase = "paused";
      this.publishSnapshot(true);
    }
  };

  private onPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    if (this.isPaused()) return;
    const bounds = this.canvas.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width * WIDTH;
    const y = (event.clientY - bounds.top) / bounds.height * HEIGHT;
    if (this.targetMode?.kind === "spell") { this.castSpellAt(this.targetMode.id, x, y); return; }
    if (this.targetMode?.kind === "hero") {
      this.hero.targetX = clamp(x, 30, WIDTH - 30);
      this.hero.targetY = clamp(y, 85, HEIGHT - 30);
      this.targetMode = null;
      this.ring(x, y, this.hero.def.accent, 0.55, 34);
      this.publishSnapshot(true);
      return;
    }
    const spot = this.level.buildSpots.map((point, index) => ({ index, distance: Math.hypot(point.x - x, point.y - y) })).filter((entry) => entry.distance < 44).sort((a, b) => a.distance - b.distance)[0];
    this.selectedSpot = spot ? (this.selectedSpot === spot.index ? null : spot.index) : null;
    this.publishSnapshot(true);
  };

  private finish(victory: boolean): void {
    if (this.resultSent) return;
    this.resultSent = true;
    this.phase = victory ? "victory" : "defeat";
    const stars = victory ? starRating(this.lives, this.startingLives) : 0;
    const score = scoreRun(this.waveIndex, this.lives, this.gold, this.elapsed, this.difficulty) + this.earlyWaves * 100;
    const result: RunResult = {
      levelId: this.level.id, difficulty: this.difficulty, mode: this.mode, victory, stars, lives: this.lives, score,
      kills: this.kills, towersBuilt: this.towersBuilt, towersUpgraded: this.towersUpgraded, spellsCast: this.spellsCast,
      discovered: [...this.discovered], bossDefeated: this.bossDefeated, elapsedSeconds: Math.round(this.elapsed),
    };
    this.options.audio.play(victory ? "victory" : "defeat");
    this.publishSnapshot(true);
    this.options.onFinish(result);
  }

  private publishSnapshot(force = false): void {
    const slice = Math.floor(this.time * 6);
    if (!force && slice === this.lastSnapshotAt) return;
    this.lastSnapshotAt = slice;
    const selected = this.selectedTower();
    const boss = this.enemies.find((enemy) => enemy.def.boss && !enemy.dead && !enemy.leaked);
    const snapshot: GameSnapshot = {
      phase: this.phase, gold: Math.round(this.gold), lives: this.lives, wave: this.waveIndex, totalWaves: this.level.waves.length,
      speed: this.speed, nextWaveIn: this.nextWaveIn, selectedSpot: this.selectedSpot,
      selectedTower: selected ? { family: selected.family, tier: selected.tier, branch: selected.branch, sellValue: Math.floor(this.towerInvestment(selected) * (0.7 + (this.upgradeRank("economy") >= 3 ? 0.1 : 0))) } : null,
      spellCooldowns: { ...this.spellCooldowns }, heroHealth: Math.round(this.hero.hp), heroMaxHealth: this.hero.maxHp, heroLevel: this.hero.level,
      boss: boss ? { name: boss.def.name, health: Math.max(0, Math.round(boss.hp)), maxHealth: boss.maxHp, phase: this.bossPhase } : undefined,
      message: this.messageUntil > this.time ? this.message : this.targetMode?.kind === "spell" ? "Choose a target" : this.targetMode?.kind === "hero" ? "Move hero" : undefined,
    };
    this.options.onSnapshot(snapshot);
  }

  private emitParticle(x: number, y: number, color: string, kind: Particle["kind"], life: number, text?: string): void {
    if (this.settings.reducedEffects && this.particles.filter((particle) => particle.alive).length > 80) return;
    let particle = this.particles.find((entry) => !entry.alive);
    const angle = this.seeded(this.particles.length + Math.floor(this.time * 100)) * Math.PI * 2;
    const speed = 18 + this.seeded(this.particles.length + 50) * 60;
    const seed: Particle = { alive: true, x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 15, life, maxLife: life, size: 2 + this.seeded(this.particles.length + 90) * 5, color, kind, text };
    if (!particle) { particle = seed; this.particles.push(particle); } else Object.assign(particle, seed);
  }

  private floatText(x: number, y: number, text: string, color: string): void { this.emitParticle(x, y, color, "damage", 0.9, text); }

  private ring(x: number, y: number, color: string, life: number, radius = 70): void {
    this.zones.push({ kind: "telegraph", x, y, radius, createdAt: this.time, activatesAt: this.time, expiresAt: this.time + life, tick: 0, color });
  }

  private updateParticles(delta: number): void {
    for (const particle of this.particles) {
      if (!particle.alive) continue;
      particle.life -= delta;
      if (particle.life <= 0) { particle.alive = false; continue; }
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
      particle.vy += 24 * delta;
    }
  }

  private render(): void {
    const context = this.context;
    context.save();
    if (this.shake > 0 && this.settings.screenShake && !this.settings.reducedMotion) {
      const amount = this.shake * (0.5 + this.seeded(Math.floor(this.time * 100)));
      context.translate((this.seeded(Math.floor(this.time * 101)) - 0.5) * amount, (this.seeded(Math.floor(this.time * 103)) - 0.5) * amount);
      this.shake = Math.max(0, this.shake - 0.45);
    }
    this.drawGround(context);
    this.drawPaths(context);
    this.drawDecorations(context);
    this.drawGoal(context);
    this.drawZones(context);
    this.drawBuildSpots(context);
    this.drawTowers(context);
    this.drawHero(context);
    this.drawEnemies(context);
    this.drawProjectiles(context);
    this.drawParticles(context);
    this.drawWeather(context);
    if (this.targetMode) {
      context.fillStyle = this.targetMode.kind === "hero" ? `${this.hero.def.accent}22` : `${spellById[this.targetMode.id]?.color ?? "#ffffff"}1e`;
      context.fillRect(0, 0, WIDTH, HEIGHT);
      context.strokeStyle = this.targetMode.kind === "hero" ? this.hero.def.accent : spellById[this.targetMode.id]?.color ?? "#fff";
      context.lineWidth = 3;
      context.setLineDash([8, 8]);
      context.strokeRect(10, 78, WIDTH - 20, HEIGHT - 88);
      context.setLineDash([]);
    }
    context.restore();
  }

  private drawGround(context: CanvasRenderingContext2D): void {
    const palette = regions[this.level.region].colors;
    const gradient = context.createLinearGradient(0, 0, WIDTH, HEIGHT);
    gradient.addColorStop(0, palette.ground);
    gradient.addColorStop(1, palette.ground2);
    context.fillStyle = gradient;
    context.fillRect(0, 0, WIDTH, HEIGHT);
    context.globalAlpha = 0.18;
    for (const decoration of this.decorations) {
      context.fillStyle = decoration.kind % 2 ? palette.accent : palette.shadow;
      context.beginPath();
      context.arc(decoration.x, decoration.y, decoration.size * 1.8, 0, Math.PI * 2);
      context.fill();
    }
    context.globalAlpha = 1;
    const vignette = context.createRadialGradient(WIDTH / 2, HEIGHT / 2, 230, WIDTH / 2, HEIGHT / 2, 780);
    vignette.addColorStop(0, "#00000000");
    vignette.addColorStop(1, "#07101877");
    context.fillStyle = vignette;
    context.fillRect(0, 0, WIDTH, HEIGHT);
  }

  private drawPaths(context: CanvasRenderingContext2D): void {
    const palette = regions[this.level.region].colors;
    for (const path of this.level.paths) {
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = `${palette.shadow}99`;
      context.lineWidth = 68;
      context.beginPath();
      context.moveTo(path[0].x, path[0].y + 8);
      path.slice(1).forEach((point) => context.lineTo(point.x, point.y + 8));
      context.stroke();
      context.strokeStyle = palette.path;
      context.lineWidth = 61;
      context.beginPath();
      context.moveTo(path[0].x, path[0].y);
      path.slice(1).forEach((point) => context.lineTo(point.x, point.y));
      context.stroke();
      context.strokeStyle = "#fff5ca44";
      context.lineWidth = 3;
      context.setLineDash([10, 18]);
      context.beginPath();
      context.moveTo(path[0].x, path[0].y - 6);
      path.slice(1).forEach((point) => context.lineTo(point.x, point.y - 6));
      context.stroke();
      context.setLineDash([]);
    }
  }

  private drawDecorations(context: CanvasRenderingContext2D): void {
    const palette = regions[this.level.region].colors;
    for (const decoration of this.decorations) {
      if (this.isNearPath(decoration.x, decoration.y, 56)) continue;
      context.save();
      context.translate(decoration.x, decoration.y);
      if (decoration.kind === 0) {
        context.fillStyle = palette.shadow;
        context.fillRect(-2, 1, 4, decoration.size + 5);
        context.fillStyle = palette.accent;
        for (let branch = 0; branch < 3; branch += 1) { context.beginPath(); context.arc((branch - 1) * decoration.size * 0.45, -branch * 3, decoration.size * (0.7 - branch * 0.1), 0, Math.PI * 2); context.fill(); }
      } else if (decoration.kind === 1) {
        context.fillStyle = palette.shadow;
        context.beginPath(); context.moveTo(-decoration.size, decoration.size * 0.5); context.lineTo(0, -decoration.size); context.lineTo(decoration.size, decoration.size * 0.5); context.closePath(); context.fill();
        context.strokeStyle = `${palette.accent}aa`; context.lineWidth = 2; context.stroke();
      } else if (decoration.kind === 2) {
        context.strokeStyle = palette.accent; context.lineWidth = 2; context.globalAlpha = 0.6;
        for (let ray = 0; ray < 5; ray += 1) { context.rotate(Math.PI * 0.4); context.beginPath(); context.moveTo(0, 0); context.lineTo(0, -decoration.size); context.stroke(); }
      } else {
        context.fillStyle = `${palette.accent}66`; context.beginPath(); context.ellipse(0, 0, decoration.size, decoration.size * 0.45, decoration.phase, 0, Math.PI * 2); context.fill();
      }
      context.restore();
    }
  }

  private isNearPath(x: number, y: number, threshold: number): boolean {
    return this.level.paths.some((path) => path.some((point) => Math.hypot(point.x - x, point.y - y) < threshold)) || this.level.buildSpots.some((point) => Math.hypot(point.x - x, point.y - y) < threshold);
  }

  private drawGoal(context: CanvasRenderingContext2D): void {
    const endpoints = this.level.paths.map((path) => path[path.length - 1]);
    for (const point of endpoints) {
      const pulse = 1 + Math.sin(this.time * 3) * 0.08;
      context.save(); context.translate(Math.min(WIDTH - 25, point.x), point.y); context.scale(pulse, pulse);
      const glow = context.createRadialGradient(0, 0, 3, 0, 0, 42); glow.addColorStop(0, "#fffbc7cc"); glow.addColorStop(0.35, "#ffd45c99"); glow.addColorStop(1, "#ffd45c00");
      context.fillStyle = glow; context.beginPath(); context.arc(0, 0, 42, 0, Math.PI * 2); context.fill();
      context.fillStyle = "#f4bd45"; context.beginPath(); context.moveTo(0, -24); context.lineTo(13, 6); context.lineTo(0, 20); context.lineTo(-13, 6); context.closePath(); context.fill();
      context.fillStyle = "#fff9cd"; context.beginPath(); context.arc(0, 2, 6, 0, Math.PI * 2); context.fill(); context.restore();
    }
  }

  private drawZones(context: CanvasRenderingContext2D): void {
    for (const zone of this.zones) {
      const progress = clamp((this.time - zone.createdAt) / Math.max(0.01, zone.expiresAt - zone.createdAt), 0, 1);
      context.save(); context.translate(zone.x, zone.y);
      context.globalAlpha = zone.kind === "telegraph" ? 1 - progress : 0.5 + Math.sin(this.time * 5) * 0.12;
      context.strokeStyle = zone.color; context.fillStyle = `${zone.color}22`; context.lineWidth = zone.kind === "hazard" && this.time < zone.activatesAt ? 5 : 3;
      context.setLineDash(zone.kind === "hazard" && this.time < zone.activatesAt ? [10, 8] : []);
      const radius = zone.kind === "telegraph" ? zone.radius * (0.35 + progress * 0.75) : zone.radius;
      context.beginPath(); context.arc(0, 0, radius, 0, Math.PI * 2); context.fill(); context.stroke(); context.setLineDash([]);
      if (zone.kind === "wardlings") for (let index = 0; index < 3; index += 1) { const angle = index / 3 * Math.PI * 2 + this.time * 0.3; this.drawSoldier(context, Math.cos(angle) * 36, Math.sin(angle) * 18, zone.color, 0.8); }
      context.restore();
    }
  }

  private drawBuildSpots(context: CanvasRenderingContext2D): void {
    this.level.buildSpots.forEach((point, index) => {
      const tower = this.towers.find((entry) => entry.spotIndex === index);
      if (tower) return;
      const selected = this.selectedSpot === index;
      context.save(); context.translate(point.x, point.y);
      context.fillStyle = selected ? "#ffe79a66" : "#20313c88"; context.strokeStyle = selected ? "#ffe17a" : "#c7b88a"; context.lineWidth = selected ? 4 : 2;
      context.beginPath(); context.ellipse(0, 0, 31, 20, 0, 0, Math.PI * 2); context.fill(); context.stroke();
      for (let stone = 0; stone < 8; stone += 1) { const angle = stone / 8 * Math.PI * 2; context.fillStyle = stone % 2 ? "#827b69" : "#aba184"; context.beginPath(); context.arc(Math.cos(angle) * 26, Math.sin(angle) * 16, 4.5, 0, Math.PI * 2); context.fill(); }
      context.fillStyle = selected ? "#ffe791" : "#d7cfac"; context.font = "bold 17px serif"; context.textAlign = "center"; context.textBaseline = "middle"; context.fillText("+", 0, 0); context.restore();
    });
    if (this.selectedSpot !== null) {
      const tower = this.selectedTower();
      if (tower) {
        const point = this.level.buildSpots[this.selectedSpot];
        context.save(); context.strokeStyle = `${towers[tower.family].color}aa`; context.fillStyle = `${towers[tower.family].color}13`; context.lineWidth = 2; context.setLineDash([8, 8]); context.beginPath(); context.arc(point.x, point.y, this.towerStats(tower).range, 0, Math.PI * 2); context.fill(); context.stroke(); context.restore();
      }
    }
  }

  private drawTowers(context: CanvasRenderingContext2D): void {
    for (const tower of this.towers) {
      const point = this.level.buildSpots[tower.spotIndex];
      const definition = towers[tower.family];
      const branch = tower.branch ? definition.branches.find((entry) => entry.id === tower.branch) : undefined;
      const color = branch?.color ?? definition.color;
      const age = this.time - tower.builtAt;
      const scale = age < 0.4 ? 0.65 + Math.sin(age / 0.4 * Math.PI) * 0.5 : 1;
      context.save(); context.translate(point.x, point.y); context.scale(scale, scale);
      context.fillStyle = "#17232b66"; context.beginPath(); context.ellipse(4, 14, 31 + tower.tier * 3, 14, 0, 0, Math.PI * 2); context.fill();
      if (tower.family === "rangers") {
        context.fillStyle = "#6e4930"; context.fillRect(-18, -12 - tower.tier * 8, 36, 35 + tower.tier * 8);
        context.fillStyle = color; context.beginPath(); context.moveTo(-27, -12 - tower.tier * 8); context.lineTo(0, -42 - tower.tier * 9); context.lineTo(27, -12 - tower.tier * 8); context.closePath(); context.fill();
        context.strokeStyle = "#f6df93"; context.lineWidth = 3; context.beginPath(); context.arc(0, -14 - tower.tier * 8, 12, Math.PI * 0.25, Math.PI * 1.75); context.stroke();
      } else if (tower.family === "arcanists") {
        context.fillStyle = "#31395c"; context.beginPath(); context.moveTo(-22, 18); context.lineTo(-15, -25 - tower.tier * 7); context.lineTo(15, -25 - tower.tier * 7); context.lineTo(22, 18); context.closePath(); context.fill();
        context.fillStyle = color; context.globalAlpha = 0.9; context.beginPath(); context.arc(0, -34 - tower.tier * 7, 11 + Math.sin(this.time * 3) * 2, 0, Math.PI * 2); context.fill();
        context.strokeStyle = "#e4fcff"; context.lineWidth = 2; context.beginPath(); context.arc(0, -34 - tower.tier * 7, 20, this.time, this.time + Math.PI * 1.4); context.stroke();
      } else if (tower.family === "artillery") {
        context.fillStyle = "#4d3b32"; context.fillRect(-27, -7 - tower.tier * 4, 54, 27 + tower.tier * 4);
        context.fillStyle = color; context.beginPath(); context.arc(-17, 17, 11, 0, Math.PI * 2); context.arc(17, 17, 11, 0, Math.PI * 2); context.fill();
        context.strokeStyle = "#2a2628"; context.lineWidth = 10 + tower.tier * 2; context.beginPath(); context.moveTo(-2, -11 - tower.tier * 4); context.lineTo(21, -35 - tower.tier * 6); context.stroke();
        context.fillStyle = "#d8b665"; context.beginPath(); context.arc(21, -35 - tower.tier * 6, 7, 0, Math.PI * 2); context.fill();
      } else {
        context.fillStyle = "#5b4637"; context.fillRect(-28, -5 - tower.tier * 7, 56, 27 + tower.tier * 7);
        context.fillStyle = color; for (let battlement = -22; battlement <= 22; battlement += 15) context.fillRect(battlement, -16 - tower.tier * 7, 10, 13);
        context.fillStyle = "#24323a"; context.fillRect(-7, 3, 14, 19);
        const count = this.towerStats(tower).block ?? 2;
        for (let soldier = 0; soldier < Math.min(4, count); soldier += 1) this.drawSoldier(context, -24 + soldier * 16, 26 + (soldier % 2) * 5, color, 0.72);
      }
      if (tower.disabledUntil > this.time) {
        context.strokeStyle = "#b76cff"; context.lineWidth = 4; context.beginPath(); context.arc(0, -10, 34, this.time * 3, this.time * 3 + Math.PI * 1.5); context.stroke();
        context.fillStyle = "#b76cff"; context.font = "bold 17px sans-serif"; context.textAlign = "center"; context.fillText("×", 0, -4);
      }
      if (this.selectedSpot === tower.spotIndex) { context.strokeStyle = "#ffe27b"; context.lineWidth = 3; context.beginPath(); context.ellipse(0, 16, 38, 20, 0, 0, Math.PI * 2); context.stroke(); }
      context.restore();
    }
  }

  private drawSoldier(context: CanvasRenderingContext2D, x: number, y: number, color: string, scale = 1): void {
    context.save(); context.translate(x, y); context.scale(scale, scale);
    context.fillStyle = "#f1c99e"; context.beginPath(); context.arc(0, -13, 7, 0, Math.PI * 2); context.fill();
    context.fillStyle = color; context.beginPath(); context.moveTo(-8, -7); context.lineTo(8, -7); context.lineTo(10, 13); context.lineTo(-10, 13); context.closePath(); context.fill();
    context.strokeStyle = "#e8ddbd"; context.lineWidth = 3; context.beginPath(); context.moveTo(8, -2); context.lineTo(15, -13); context.stroke(); context.restore();
  }

  private drawHero(context: CanvasRenderingContext2D): void {
    if (this.hero.hp <= 0) {
      const remaining = Math.max(0, Math.ceil(this.hero.respawnAt - this.time));
      context.fillStyle = "#ffffffcc"; context.font = "bold 18px sans-serif"; context.textAlign = "center"; context.fillText(`${remaining}`, this.hero.targetX, this.hero.targetY); return;
    }
    const hero = this.hero;
    context.save(); context.translate(hero.x, hero.y);
    context.fillStyle = "#17232b77"; context.beginPath(); context.ellipse(3, 13, 20, 8, 0, 0, Math.PI * 2); context.fill();
    context.fillStyle = hero.def.color; context.beginPath(); context.moveTo(-14, 12); context.lineTo(-11, -14); context.lineTo(11, -14); context.lineTo(15, 12); context.closePath(); context.fill();
    context.fillStyle = "#eec69d"; context.beginPath(); context.arc(0, -20, 9, 0, Math.PI * 2); context.fill();
    context.strokeStyle = hero.def.accent; context.lineWidth = 4;
    if (hero.def.role === "tank") { context.beginPath(); context.arc(-12, -1, 10, 0, Math.PI * 2); context.stroke(); }
    else if (hero.def.role === "ranged") { context.beginPath(); context.arc(11, -3, 13, -Math.PI / 2, Math.PI / 2); context.stroke(); }
    else { context.beginPath(); context.arc(11, -17, 5 + Math.sin(this.time * 4), 0, Math.PI * 2); context.stroke(); }
    context.strokeStyle = "#fff4ae"; context.lineWidth = 2; context.beginPath(); context.arc(0, -4, 23, this.time * 0.5, this.time * 0.5 + Math.PI * 1.3); context.stroke();
    this.drawHealthBar(context, -23, -39, 46, hero.hp / hero.maxHp, hero.def.accent);
    context.restore();
  }

  private drawEnemies(context: CanvasRenderingContext2D): void {
    for (const enemy of this.enemies) {
      if (enemy.dead || enemy.leaked) continue;
      context.save(); context.translate(enemy.x, enemy.y); context.globalAlpha = enemy.alpha * (enemy.def.traits.includes("stealth") ? 0.76 : 1);
      const bob = Math.sin(this.time * 7 + enemy.id) * (enemy.def.traits.includes("flying") ? 5 : 1.5);
      context.translate(0, bob);
      context.fillStyle = "#10151d66"; context.beginPath(); context.ellipse(2, enemy.def.size * 0.55, enemy.def.size * 0.8, enemy.def.size * 0.3, 0, 0, Math.PI * 2); context.fill();
      if (enemy.def.silhouette === "wing") {
        context.fillStyle = `${enemy.def.accent}bb`; const flap = Math.sin(this.time * 13 + enemy.id) * 0.35;
        context.save(); context.rotate(flap); context.beginPath(); context.ellipse(-enemy.def.size * 0.75, -3, enemy.def.size * 0.65, enemy.def.size * 0.3, -0.4, 0, Math.PI * 2); context.fill(); context.restore();
        context.save(); context.rotate(-flap); context.beginPath(); context.ellipse(enemy.def.size * 0.75, -3, enemy.def.size * 0.65, enemy.def.size * 0.3, 0.4, 0, Math.PI * 2); context.fill(); context.restore();
      }
      context.fillStyle = enemy.def.color;
      if (enemy.def.silhouette === "serpent") { context.beginPath(); context.ellipse(0, 0, enemy.def.size * 1.05, enemy.def.size * 0.6, Math.sin(this.time + enemy.id) * 0.18, 0, Math.PI * 2); context.fill(); }
      else if (enemy.def.silhouette === "construct") { context.fillRect(-enemy.def.size * 0.65, -enemy.def.size * 0.65, enemy.def.size * 1.3, enemy.def.size * 1.3); }
      else if (enemy.def.silhouette === "brute" || enemy.def.silhouette === "boss") { context.beginPath(); context.moveTo(-enemy.def.size * 0.8, enemy.def.size * 0.65); context.lineTo(-enemy.def.size * 0.55, -enemy.def.size * 0.65); context.lineTo(enemy.def.size * 0.55, -enemy.def.size * 0.65); context.lineTo(enemy.def.size * 0.8, enemy.def.size * 0.65); context.closePath(); context.fill(); }
      else { context.beginPath(); context.ellipse(0, 0, enemy.def.size * 0.65, enemy.def.size * 0.8, 0, 0, Math.PI * 2); context.fill(); }
      context.fillStyle = enemy.def.accent;
      if (enemy.def.silhouette === "caster") { context.beginPath(); context.moveTo(-enemy.def.size * 0.7, -enemy.def.size * 0.35); context.lineTo(0, -enemy.def.size * 1.15); context.lineTo(enemy.def.size * 0.7, -enemy.def.size * 0.35); context.closePath(); context.fill(); }
      else if (["beast", "brute", "boss"].includes(enemy.def.silhouette)) { context.beginPath(); context.moveTo(-enemy.def.size * 0.5, -enemy.def.size * 0.55); context.lineTo(-enemy.def.size * 0.8, -enemy.def.size * 1.05); context.lineTo(-enemy.def.size * 0.1, -enemy.def.size * 0.66); context.moveTo(enemy.def.size * 0.5, -enemy.def.size * 0.55); context.lineTo(enemy.def.size * 0.8, -enemy.def.size * 1.05); context.lineTo(enemy.def.size * 0.1, -enemy.def.size * 0.66); context.fill(); }
      context.fillStyle = "#fff7d0"; context.beginPath(); context.arc(-enemy.def.size * 0.2, -enemy.def.size * 0.17, Math.max(2, enemy.def.size * 0.1), 0, Math.PI * 2); context.arc(enemy.def.size * 0.2, -enemy.def.size * 0.17, Math.max(2, enemy.def.size * 0.1), 0, Math.PI * 2); context.fill();
      context.fillStyle = "#16202a"; context.beginPath(); context.arc(-enemy.def.size * 0.2, -enemy.def.size * 0.17, 1.5, 0, Math.PI * 2); context.arc(enemy.def.size * 0.2, -enemy.def.size * 0.17, 1.5, 0, Math.PI * 2); context.fill();
      if (enemy.shield > 0) { context.strokeStyle = "#83e8f1cc"; context.lineWidth = 3; context.beginPath(); context.arc(0, 0, enemy.def.size * 1.05, this.time, this.time + Math.PI * 1.6); context.stroke(); }
      if (enemy.statuses.length) {
        const colors: Record<string, string> = { poison: "#78d66d", burn: "#ff6d3c", slow: "#8eeaff", stun: "#ffe56e", root: "#86be5e", armorBreak: "#f0a86b", magicBreak: "#be8aff", mark: "#fff6a8" };
        enemy.statuses.slice(0, 4).forEach((status, index) => { context.fillStyle = colors[status.kind]; context.beginPath(); context.arc(-12 + index * 8, -enemy.def.size - 12, 3, 0, Math.PI * 2); context.fill(); });
      }
      this.drawHealthBar(context, -enemy.def.size, -enemy.def.size - 8, enemy.def.size * 2, enemy.hp / enemy.maxHp, enemy.def.boss ? "#d678ff" : "#72d074");
      context.restore();
    }
  }

  private drawHealthBar(context: CanvasRenderingContext2D, x: number, y: number, width: number, ratio: number, color: string): void {
    context.fillStyle = "#13202ccc"; context.fillRect(x, y, width, 5); context.fillStyle = color; context.fillRect(x + 1, y + 1, Math.max(0, (width - 2) * clamp(ratio, 0, 1)), 3);
  }

  private drawProjectiles(context: CanvasRenderingContext2D): void {
    for (const projectile of this.projectiles) {
      if (!projectile.alive) continue;
      context.strokeStyle = `${projectile.color}aa`; context.lineWidth = projectile.family === "artillery" ? 5 : 2; context.beginPath(); context.moveTo(projectile.previousX, projectile.previousY); context.lineTo(projectile.x, projectile.y); context.stroke();
      context.fillStyle = projectile.color; context.beginPath(); context.arc(projectile.x, projectile.y, projectile.family === "artillery" ? 7 : projectile.family === "arcanists" ? 5 : 3, 0, Math.PI * 2); context.fill();
    }
  }

  private drawParticles(context: CanvasRenderingContext2D): void {
    for (const particle of this.particles) {
      if (!particle.alive) continue;
      const alpha = clamp(particle.life / particle.maxLife, 0, 1);
      context.save(); context.globalAlpha = alpha; context.translate(particle.x, particle.y);
      if (particle.kind === "damage") { context.fillStyle = particle.color; context.strokeStyle = "#17212bdd"; context.lineWidth = 3; context.font = "900 16px sans-serif"; context.textAlign = "center"; context.strokeText(particle.text ?? "", 0, 0); context.fillText(particle.text ?? "", 0, 0); }
      else { context.fillStyle = particle.color; context.beginPath(); context.arc(0, 0, particle.size * alpha, 0, Math.PI * 2); context.fill(); }
      context.restore();
    }
  }

  private drawWeather(context: CanvasRenderingContext2D): void {
    const weather = regions[this.level.region].weather;
    context.save();
    for (const particle of this.weather) {
      const speed = weather === "snow" ? 18 : weather === "sand" ? 60 : 25;
      const x = (particle.x + this.time * speed * (weather === "sand" ? 1 : 0.25)) % WIDTH;
      const y = (particle.y + this.time * speed * (weather === "snow" ? 1 : 0.15)) % HEIGHT;
      context.globalAlpha = 0.25 + Math.sin(this.time * 2 + particle.phase) * 0.12;
      context.fillStyle = weather === "embers" ? "#ff8048" : weather === "rift" ? "#ba77ff" : weather === "snow" ? "#ffffff" : weather === "fireflies" ? "#b8ff8a" : weather === "sand" ? "#f2c77a" : "#d8f5df";
      context.beginPath(); context.arc(x, y, particle.size, 0, Math.PI * 2); context.fill();
    }
    context.restore();
  }

  getAllowedTowers(): TowerFamily[] { return [...this.allowedTowers()]; }
  getHeroAbilityCooldowns(): Record<string, number> { return { ...this.hero.abilityCooldowns }; }
  getTowerTargeting(): string { return this.selectedTower()?.targeting ?? "first"; }
  getTargetMode(): string | null { return this.targetMode?.kind === "spell" ? this.targetMode.id : this.targetMode?.kind ?? null; }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.frame);
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }
}
